import React, { useState, useEffect, useRef } from "react";
import { useHRMS } from "../context/HRMSContext.jsx";
import api from "../services/api.js";
import DatePicker from "../components/DatePicker.jsx";
import Message from "../components/Message.jsx";
import PermissionGuard from "../components/PermissionGuard.jsx";
import { MODULES, ACTIONS } from "../context/PermissionsContext.jsx";
import CommonTable from '../components/common/CommonTable.jsx';

function Attendance() {
  const { state, loadWorkers, loadShifts, loadLeaves } = useHRMS();

  // Get today's date in local timezone (YYYY-MM-DD format)
  const getTodayDate = () => {
    const today = new Date();
    // Use local timezone, not UTC
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [showMarkForm, setShowMarkForm] = useState(false);
  const [attendanceData, setAttendanceData] = useState({
    worker_id: "",
    date: todayDate,
    checkIn: "",
    checkOut: "",
    otCheckIn: "",
    otCheckOut: "",
    status: 0,
  });
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [overrideLeave, setOverrideLeave] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [attendanceFilters, setAttendanceFilters] = useState({
    worker_id: "",
    worker_name: "",
    working_hours: "",
  });
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("Loading...");

  const selectedWorkerObj = state.workers?.find((w) => w.id === attendanceData.worker_id);
  const selectedWorkerJoiningDate = selectedWorkerObj?.joining_date || selectedWorkerObj?.joiningDate || selectedWorkerObj?.joinDate;
  const workerMinDate = selectedWorkerJoiningDate ? String(selectedWorkerJoiningDate).split("T")[0] : null;

  const initialLoadRef = useRef(false);

  // 1. Initial page load: Load ONLY what is needed for Attendance List & Filters (Runs ONCE on mount)
  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;

    if (!state.workers || state.workers.length === 0) {
      loadWorkers();
    }
    if (!state.shifts || state.shifts.length === 0) {
      loadShifts();
    }
  }, [loadWorkers, loadShifts]);

  // 2. Action-based: Load approved leaves only when user opens Mark Attendance modal
  useEffect(() => {
    if (showMarkForm) {
      if (!state.leaves || state.leaves.length === 0) {
        loadLeaves();
      }
    }
  }, [showMarkForm, loadLeaves, state.leaves]);

  const handleMarkAttendance = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Check if attendance already exists for this worker on this date (only for new entries)
    if (!editingAttendance) {
      const existingAttendance = state.attendance?.find((att) => {
        const attDate = att.date?.split("T")[0] || att.date; // Handle both date formats
        return (
          att.worker_id === attendanceData.worker_id &&
          attDate === attendanceData.date
        );
      });

      if (existingAttendance) {
        setMessage({
          text: `Attendance already exists for this worker on ${attendanceData.date}!`,
          type: "error",
        });
        return;
      }
    }

    // Check if worker has approved leave for this date
    const approvedLeave = state.leaves?.find((leave) => {
      const leaveStartDate = leave.startDt?.split('T')[0] || leave.startDt;
      const leaveEndDate = leave.endDt?.split('T')[0] || leave.endDt;
      const attendanceDate = attendanceData.date;

      return (
        leave.workerId === attendanceData.worker_id &&
        leave.leaveStatus === 1 && // Approved leave
        attendanceDate >= leaveStartDate &&
        attendanceDate <= leaveEndDate
      );
    });

    if (approvedLeave) {
      // Allow attendance for half-day leaves
      if (approvedLeave.session === 'First Half' || approvedLeave.session === 'Second Half') {
        const worker = state.workers?.find((w) => w.id === attendanceData.worker_id);
        setMessage({
          text: `Note: ${worker?.name || 'Worker'} has approved ${approvedLeave.session} leave on ${attendanceData.date}. Please mark attendance for the working half only.`,
          type: "warning",
        });
      } else if (!overrideLeave) {
        const worker = state.workers?.find((w) => w.id === attendanceData.worker_id);
        setMessage({
          text: `Cannot mark attendance for ${worker?.name || 'this worker'} on ${attendanceData.date}. Worker has approved full-day leave. Check the box below to override.`,
          type: "error",
        });
        return;
      }
    }

    // Get worker's shift information
    const worker = state.workers?.find((w) => w.id === attendanceData.worker_id);
    const workerShift = worker?.shiftId
      ? state.shifts?.find((s) => s.id === worker.shiftId)
      : null;

    // Check if attendance date is before worker's joining date
    const workerJoiningDate = worker?.joining_date || worker?.joiningDate || worker?.joinDate;
    if (worker && workerJoiningDate && attendanceData.date) {
      const joinDateStr = String(workerJoiningDate).split("T")[0];
      const attendanceDateStr = String(attendanceData.date).split("T")[0];

      if (attendanceDateStr < joinDateStr) {
        setMessage({
          text: `Cannot mark attendance before worker's joining date (${joinDateStr})!`,
          type: "error",
        });
        return;
      }
    }

    // Validate OT times against shift end time
    if (
      workerShift &&
      (attendanceData.otCheckIn || attendanceData.otCheckOut)
    ) {
      const shiftEndTime = workerShift.shiftEnd;

      // Check OT Check In
      if (attendanceData.otCheckIn && attendanceData.otCheckIn < shiftEndTime) {
        setMessage({
          text: `OT Check In (${attendanceData.otCheckIn}) cannot be before shift end time (${shiftEndTime})!`,
          type: "error",
        });
        return;
      }

      // Check OT Check Out
      if (
        attendanceData.otCheckOut &&
        attendanceData.otCheckIn &&
        attendanceData.otCheckOut < attendanceData.otCheckIn
      ) {
        setMessage({
          text: `OT Check Out (${attendanceData.otCheckOut}) cannot be before OT Check In (${attendanceData.otCheckIn})!`,
          type: "error",
        });
        return;
      }

      // If OT Check In is provided, OT Check Out should also be provided
      if (attendanceData.otCheckIn && !attendanceData.otCheckOut) {
        setMessage({
          text: "OT Check Out is required when OT Check In is provided!",
          type: "error",
        });
        return;
      }
    }

    //   if (attendanceData.checkIn && attendanceData.checkOut) {
    //   const inTime = attendanceData.checkIn;
    //   const outTime = attendanceData.checkOut;

    //   // If Check-In is greater than or equal to Check-Out
    //   if (inTime >= outTime) {
    //     alert("❌ Check-In time cannot be greater than or equal to Check-Out time!");
    //     return; // STOP form submit

    //   }
    // }

    try {
      setIsSubmitting(true);
      // Create payload with selected date
      const payload = {
        worker_id: attendanceData.worker_id,
        date: attendanceData.date,
        checkIn: attendanceData.checkIn,
        checkOut: attendanceData.checkOut,
        otCheckIn: attendanceData.otCheckIn || null,
        otCheckOut: attendanceData.otCheckOut || null,
        status: attendanceData.status,
      };

      if (editingAttendance) {
        await api.attendance.update(editingAttendance.id, payload);
        setMessage({
          text: `Attendance updated successfully for ${attendanceData.date}!`,
          type: "success",
        });
      } else {
        await api.attendance.create(payload);
        setMessage({
          text: `Attendance marked successfully for ${attendanceData.date}!`,
          type: "success",
        });
      }

      setSelectedDate(attendanceData.date);

      // Reset form with today's date for next entry
      const newTodayDate = getTodayDate();
      setAttendanceData({
        worker_id: "",
        date: newTodayDate,
        checkIn: "",
        checkOut: "",
        otCheckIn: "",
        otCheckOut: "",
        status: 0,
      });
      setEditingAttendance(null);
      setOverrideLeave(false);
      setShowMarkForm(false);

      // Auto remove success message after 3 seconds
      setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 3000);
    } catch (error) {
      console.error("❌ Error marking attendance:", error);
      setMessage({
        text: `Error marking attendance: ${error.message || "Unknown error"}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAttendance = async (attendance) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText("Loading attendance details...");
    try {
      const promises = [];
      if (!state.leaves || state.leaves.length === 0) promises.push(loadLeaves().catch(() => { }));
      if (!state.workers || state.workers.length === 0) promises.push(loadWorkers().catch(() => { }));
      if (!state.shifts || state.shifts.length === 0) promises.push(loadShifts().catch(() => { }));

      // Ensure smooth visual transition and allow render of blocking loader
      promises.push(new Promise((resolve) => setTimeout(resolve, 250)));
      await Promise.all(promises);

      setEditingAttendance(attendance);
      const attDate = attendance.date?.split("T")[0] || attendance.date;
      setAttendanceData({
        worker_id: attendance.worker_id,
        date: attDate,
        checkIn: attendance.checkIn || "",
        checkOut: attendance.checkOut || "",
        otCheckIn: attendance.otCheckIn || "",
        otCheckOut: attendance.otCheckOut || "",
        status: attendance.status || 0,
      });
      setShowMarkForm(true);
    } catch (error) {
      console.error("Error opening edit attendance:", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const getWorkerName = (attendance) => {
    // First try to get name from attendance object (from backend)
    if (attendance.workerName) {
      return attendance.workerName;
    }
    // Fallback to finding worker by ID
    const worker = state.workers?.find((w) => w.id === attendance.worker_id);
    return worker ? worker.name : "Unknown";
  };

  const calculateWorkingHours = (checkIn, checkOut, workerId) => {
    if (!checkIn) return "0";

    let effectiveCheckOut = checkOut;
    if (!checkOut) {
      const worker = state.workers?.find((w) => w.id === workerId);
      const shift = worker?.shiftId
        ? state.shifts?.find((s) => s.id === worker.shiftId)
        : null;

      if (shift?.shiftEnd) {
        effectiveCheckOut = shift.shiftEnd;
      } else {
        return "0";
      }
    }

    const [inHours, inMinutes] = checkIn.split(":").map(Number);
    const [outHours, outMinutes] = effectiveCheckOut.split(":").map(Number);

    const inTotal = inHours * 60 + inMinutes;
    let outTotal = outHours * 60 + outMinutes;

    if (outTotal < inTotal) {
      outTotal += 24 * 60;
    }

    const diffMinutes = outTotal - inTotal;
    return (diffMinutes / 60).toFixed(1);
  };

  const calculateOTHours = (otCheckIn, otCheckOut) => {
    if (!otCheckIn || !otCheckOut) return "0";

    const [inHours, inMinutes] = otCheckIn.split(":").map(Number);
    const [outHours, outMinutes] = otCheckOut.split(":").map(Number);

    const inTotal = inHours * 60 + inMinutes;
    let outTotal = outHours * 60 + outMinutes;

    if (outTotal < inTotal) {
      outTotal += 24 * 60;
    }

    const diffMinutes = outTotal - inTotal;
    return (diffMinutes / 60).toFixed(1);
  };

  const formatWorkingHours = (checkIn, checkOut, workerId) => {
    if (!checkIn) return "-";

    let effectiveCheckOut = checkOut;
    if (!checkOut) {
      const worker = state.workers?.find((w) => w.id === workerId);
      const shift = worker?.shiftId
        ? state.shifts?.find((s) => s.id === worker.shiftId)
        : null;

      if (shift?.shiftEnd) {
        effectiveCheckOut = shift.shiftEnd;
      } else {
        return "-";
      }
    }

    const [inHours, inMinutes] = checkIn.split(":").map(Number);
    const [outHours, outMinutes] = effectiveCheckOut.split(":").map(Number);

    if (isNaN(inHours) || isNaN(inMinutes) || isNaN(outHours) || isNaN(outMinutes)) {
      return "-";
    }

    const inTotal = inHours * 60 + inMinutes;
    let outTotal = outHours * 60 + outMinutes;

    if (outTotal < inTotal) {
      outTotal += 24 * 60;
    }

    const diffMinutes = outTotal - inTotal;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = Math.round(diffMinutes % 60);
    return `${hours} hrs ${minutes} minutes`;
  };

  const formatOTHours = (otCheckIn, otCheckOut) => {
    if (!otCheckIn || !otCheckOut) return "-";

    const [inHours, inMinutes] = otCheckIn.split(":").map(Number);
    const [outHours, outMinutes] = otCheckOut.split(":").map(Number);

    if (isNaN(inHours) || isNaN(inMinutes) || isNaN(outHours) || isNaN(outMinutes)) {
      return "-";
    }

    const inTotal = inHours * 60 + inMinutes;
    let outTotal = outHours * 60 + outMinutes;

    if (outTotal < inTotal) {
      outTotal += 24 * 60;
    }

    const diffMinutes = outTotal - inTotal;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = Math.round(diffMinutes % 60);
    return `${hours} hrs ${minutes} minutes`;
  };

  const hasActiveFilters = Object.values(attendanceFilters).some(
    (value) => String(value).trim() !== ""
  );

  return (
    <PermissionGuard module={MODULES.ATTENDANCE} action={ACTIONS.VIEW} submodule={MODULES.ATTENDANCE.submodules.DAILY_ATTENDANCE}>
      <div className="attendance">
        {/* Full-screen blocking loader when action is triggered */}
        {isActionLoading && (
          <div
            className="full-screen-action-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              inset: 0,
              width: "100%",
              height: "100%",
              minWidth: "100%",
              minHeight: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 999999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "all",
              cursor: "wait",
            }}
          >
            <div
              className="full-screen-action-card"
              style={{
                background: "#ffffff",
                padding: "18px 28px",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                pointerEvents: "auto",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2rem", height: "2rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <span
                className="full-screen-action-text"
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#212529",
                  letterSpacing: "0.2px",
                }}
              >
                {actionLoadingText || "Loading..."}
              </span>
            </div>
          </div>
        )}

        {message.text && (
          <Message
            message={message.text}
            type={message.type}
            onClose={() => setMessage({ text: "", type: "" })}
          />
        )}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h2 class="hd_a">Attendance Management</h2>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className={`btn ${hasActiveFilters ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setShowFilters((prev) => !prev)}
              title="Filter Attendance"
            >
              <i className="bi bi-funnel"></i>
            </button>
            <PermissionGuard module={MODULES.ATTENDANCE} action={ACTIONS.CREATE} submodule={MODULES.ATTENDANCE.submodules.DAILY_ATTENDANCE}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const today = getTodayDate();
                  setAttendanceData({
                    worker_id: "",
                    date: today,
                    checkIn: "",
                    checkOut: "",
                    otCheckIn: "",
                    otCheckOut: "",
                    status: 0,
                  });
                  setEditingAttendance(null);
                  setOverrideLeave(false);
                  setShowMarkForm(true);
                }}
              >
                <i className="bi bi-plus-lg me-1"></i>Mark Attendance
              </button>
            </PermissionGuard>
          </div>
        </div>

        {showFilters && (
          <div className="card shadow-sm mb-3">
            <div className="card-body py-2">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div style={{ minWidth: "200px", flex: "0 1 200px" }}>
                  <label className="form-label mb-1">Worker/Employee Name</label>
                  <select
                    className="form-control form-control-sm"
                    value={attendanceFilters.worker_id}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const matched = (state.workers || []).find((w) => String(w.id) === String(selectedId));
                      const cleanName = matched?.worker_name || matched?.name?.replace(/\s*\((sigi|sigipro|hrms user)\)$/i, '') || "";
                      setAttendanceFilters((prev) => ({
                        ...prev,
                        worker_id: selectedId,
                        worker_name: cleanName,
                      }));
                    }}
                  >
                    <option value="">All Workers</option>

                    {(state.workers || []).map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {worker.name}
                      </option>
                    ))}
                  </select></div>
                <div style={{ minWidth: "160px", flex: "0 1 160px" }}>
                  <label className="form-label mb-1">Working Hours</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    className="form-control form-control-sm"
                    placeholder="Filter by working hours"
                    value={attendanceFilters.working_hours}
                    onChange={(e) =>
                      setAttendanceFilters((prev) => ({
                        ...prev,
                        working_hours: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="d-flex align-items-end" style={{ minWidth: "90px" }}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm w-100"
                    onClick={() =>
                      setAttendanceFilters({
                        worker_id: "",
                        worker_name: "",
                        working_hours: "",
                      })
                    }
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {showMarkForm && (
          <div
            className="modal"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", pointerEvents: isSubmitting ? "none" : "auto" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white pt-2 pr-0 pb-2">
                  <h5 className="modal-title">
                    {editingAttendance ? "Edit" : "Mark"} Attendance
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={isSubmitting}
                    onClick={() => {
                      if (isSubmitting) return;
                      setShowMarkForm(false);
                      setOverrideLeave(false);
                      setAttendanceData({
                        worker_id: "",
                        date: getTodayDate(),
                        checkIn: "",
                        checkOut: "",
                        otCheckIn: "",
                        otCheckOut: "",
                        status: 0,
                      });
                    }}
                  ></button>
                </div>
                <form onSubmit={handleMarkAttendance}>
                  <div className="modal-body">
                    <div className="row g-2">
                      <div className="col-md-3">
                        <label className="form-label">Date *</label>
                        <DatePicker
                          value={attendanceData.date}
                          onChange={(date) =>
                            setAttendanceData({ ...attendanceData, date })
                          }
                          placeholder="Select attendance date"
                          maxDate={getTodayDate()} // ⛔ Prevent future dates
                          minDate={workerMinDate} // ⛔ Prevent dates before worker's joining date
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Worker /Employee*</label>
                        <select
                          className="form-control"
                          value={attendanceData.worker_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            const parsedWorkerId = String(val).startsWith('user_') ? val : parseInt(val);
                            const chosenWorker = state.workers?.find((w) => w.id === parsedWorkerId);
                            const chosenJoiningDate = chosenWorker?.joining_date || chosenWorker?.joiningDate || chosenWorker?.joinDate;
                            const joinDateStr = chosenJoiningDate ? String(chosenJoiningDate).split("T")[0] : null;

                            setAttendanceData((prev) => {
                              const next = {
                                ...prev,
                                worker_id: parsedWorkerId,
                              };
                              if (joinDateStr && prev.date && prev.date < joinDateStr) {
                                next.date = todayDate >= joinDateStr ? todayDate : joinDateStr;
                              }
                              return next;
                            });
                          }}
                          required
                        >
                          <option value="">Select Worker/Employee</option>
                          {Array.isArray(state.workers) &&
                            state.workers.map((worker) => {
                              // Check if worker has half-day leave for selected date
                              const halfDayLeave = state.leaves?.find((leave) => {
                                const leaveStartDate = leave.startDt?.split('T')[0] || leave.startDt;
                                const leaveEndDate = leave.endDt?.split('T')[0] || leave.endDt;
                                const selectedAttendanceDate = attendanceData.date;

                                return (
                                  leave.workerId === worker.id &&
                                  leave.leaveStatus === 1 && // Approved leave
                                  selectedAttendanceDate >= leaveStartDate &&
                                  selectedAttendanceDate <= leaveEndDate &&
                                  (leave.session === 'First Half' || leave.session === 'Second Half')
                                );
                              });

                              const displayName = halfDayLeave
                                ? `${worker.name} (${halfDayLeave.session} Leave)`
                                : worker.name;

                              return (
                                <option key={worker.id} value={worker.id}>
                                  {displayName}
                                </option>
                              );
                            })}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Shift</label>
                        <input
                          type="text"
                          className="form-control"
                          value={(() => {
                            const worker = state.workers?.find((w) => w.id === attendanceData.worker_id);
                            const shift = worker?.shiftId ? state.shifts?.find((s) => s.id === worker.shiftId) : null;
                            return shift ? `${shift.shiftName} (${shift.shiftStart}-${shift.shiftEnd})` : '';
                          })()}
                          readOnly
                          placeholder="No shift assigned"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-control"
                          value={attendanceData.status}
                          onChange={(e) =>
                            setAttendanceData({
                              ...attendanceData,
                              status: parseInt(e.target.value),
                            })
                          }
                          required
                        >
                          <option value={0}>Present</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Check In</label>
                        <input
                          type="time"
                          className="form-control"
                          value={attendanceData.checkIn}
                          onChange={(e) =>
                            setAttendanceData({
                              ...attendanceData,
                              checkIn: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Check Out</label>
                        <input
                          type="time"
                          className="form-control"
                          value={attendanceData.checkOut}
                          onChange={(e) =>
                            setAttendanceData({
                              ...attendanceData,
                              checkOut: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">OT Check In</label>
                        <input
                          type="time"
                          className="form-control"
                          value={attendanceData.otCheckIn}
                          onChange={(e) =>
                            setAttendanceData({
                              ...attendanceData,
                              otCheckIn: e.target.value,
                            })
                          }
                        />
                        {(() => {
                          const worker = state.workers?.find(
                            (w) => w.id === attendanceData.worker_id
                          );
                          const shift = worker?.shiftId
                            ? state.shifts?.find((s) => s.id === worker.shiftId)
                            : null;
                          return shift ? (
                            <small className="text-muted">
                              OT can only start after shift end: {shift.shiftEnd}
                            </small>
                          ) : null;
                        })()}
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">OT Check Out</label>
                        <input
                          type="time"
                          className="form-control"
                          value={attendanceData.otCheckOut}
                          onChange={(e) =>
                            setAttendanceData({
                              ...attendanceData,
                              otCheckOut: e.target.value,
                            })
                          }
                        />
                        <small className="text-muted">
                          Required if OT Check In is provided
                        </small>
                      </div>
                      {(() => {
                        const approvedLeave = state.leaves?.find((leave) => {
                          const leaveStartDate = leave.startDt?.split('T')[0] || leave.startDt;
                          const leaveEndDate = leave.endDt?.split('T')[0] || leave.endDt;
                          const selectedAttendanceDate = attendanceData.date;

                          return (
                            leave.workerId === attendanceData.worker_id &&
                            leave.leaveStatus === 1 &&
                            selectedAttendanceDate >= leaveStartDate &&
                            selectedAttendanceDate <= leaveEndDate &&
                            leave.session !== 'First Half' &&
                            leave.session !== 'Second Half'
                          );
                        });

                        return approvedLeave ? (
                          <div className="col-12">
                            <div className="alert alert-warning">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="overrideLeave"
                                  checked={overrideLeave}
                                  onChange={(e) => setOverrideLeave(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="overrideLeave">
                                  <strong>Worker/Employee has approved leave on this date.</strong> Check this box to mark attendance anyway.
                                </label>
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div className="modal-footer pt-2 pr-0 pb-2">
                    <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                      {isSubmitting && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      )}
                      {isSubmitting ? (editingAttendance ? 'Updating...' : 'Saving...') : (editingAttendance ? 'Update' : 'Save')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isSubmitting}
                      onClick={() => {
                        if (isSubmitting) return;
                        setShowMarkForm(false);
                        setOverrideLeave(false);
                        setAttendanceData({
                          worker_id: "",
                          date: getTodayDate(),
                          checkIn: "",
                          checkOut: "",
                          otCheckIn: "",
                          otCheckOut: "",
                          status: 0,
                        });
                      }}
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center" style={{ overflow: 'visible' }}>
            <h5 className="mb-0 fs-6">Attendance for {selectedDate}</h5>
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 text-muted" style={{ fontSize: "13px", whiteSpace: "nowrap" }}>Select Date:</label>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                placeholder="Select date"
              />
            </div>
          </div>
          <div className="card-body px-0 pt-0">
            <CommonTable
              showRefresh={true}
              dataSource={api.attendance.table}
              queryParams={{
                date: selectedDate,
                worker_id: attendanceFilters.worker_id || undefined,
                worker_name: !attendanceFilters.worker_id ? attendanceFilters.worker_name : undefined,
                working_hours: attendanceFilters.working_hours,
              }}
              columns={[
                { key: 'workerName', label: 'Worker/Employee', sortable: true, sortKey: 'worker.worker_name', render: (attendance) => getWorkerName(attendance) },
                { key: 'checkIn', label: 'Check In', sortable: true, render: (attendance) => attendance.checkIn || '-' },
                { key: 'checkOut', label: 'Check Out', sortable: true, render: (attendance) => attendance.checkOut || '-' },
                { key: 'otCheckIn', label: 'OT In', sortable: true, render: (attendance) => attendance.otCheckIn || '-' },
                { key: 'otCheckOut', label: 'OT Out', sortable: true, render: (attendance) => attendance.otCheckOut || '-' },
                { key: 'status', label: 'Status', sortable: true, render: () => <span className="badge bg-success">Present</span> },
                { key: 'workingHours', label: 'Working Hours', sortable: false, render: (attendance, row) => formatWorkingHours((row || attendance).checkIn, (row || attendance).checkOut, (row || attendance).worker_id) },
                { key: 'otHours', label: 'OT Hours', sortable: false, render: (attendance, row) => formatOTHours((row || attendance).otCheckIn, (row || attendance).otCheckOut) }
              ]}
              renderActions={(attendance) => (
                <PermissionGuard module={MODULES.ATTENDANCE} action={ACTIONS.EDIT} submodule={MODULES.ATTENDANCE.submodules.DAILY_ATTENDANCE}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEditAttendance(attendance)}
                    disabled={isActionLoading}
                    style={{ fontSize: '13px', padding: '4px 10px' }}
                    title="Edit Attendance"
                  >
                    <i className="bi bi-pencil-square"></i>
                  </button>
                </PermissionGuard>
              )}
              emptyMessage={`No attendance records found for ${selectedDate}`}
            />
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
}

export default Attendance;
